import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { AssistantEvent, AssistantTask } from '@accounting/shared';
import { PROVIDER_LABELS } from '../../hooks/useAssistant';
import { useCreateTask, useDeleteTask, useToday, useUpdateTask } from '../../hooks/useAssistantTasks';
import { TaskEditor } from '../../components/assistant/TaskEditor';
import { TaskRow } from '../../components/assistant/TaskRow';
import { ApiError } from '../../lib/api';
import { addDaysKey, dayHeading, formatTimeRange, fromDateKey, localDateKey } from '../../lib/calendarGrid';

export const Route = createFileRoute('/_auth/assistant/today')({ component: TodayPage });

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    return body?.error ?? `Request failed (${err.status})`;
  }
  return err instanceof Error ? err.message : String(err);
}

function TodayPage() {
  const realToday = localDateKey(new Date());
  const [date, setDate] = useState(realToday);
  const today = useToday(date);
  const create = useCreateTask();
  const update = useUpdateTask();
  const remove = useDeleteTask();
  const [editing, setEditing] = useState<AssistantTask | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [quick, setQuick] = useState('');
  const busy = create.isPending || update.isPending || remove.isPending;

  const toggle = (t: AssistantTask) => update.mutate({ id: t.id, status: t.status === 'completed' ? 'open' : 'completed' }, { onError: (e) => setMessage(errorMessage(e)) });
  const del = (t: AssistantTask) => {
    if (window.confirm(`Delete "${t.title}"?`)) remove.mutate(t.id, { onError: (e) => setMessage(errorMessage(e)) });
  };

  const d = today.data;
  const upcomingByDay = new Map<string, AssistantEvent[]>();
  for (const ev of d?.upcoming ?? []) {
    const key = localDateKey(new Date(ev.startsAt));
    const list = upcomingByDay.get(key);
    if (list) list.push(ev);
    else upcomingByDay.set(key, [ev]);
  }

  return (
    <div className="pa-today">
      <header className="pa-toolbar">
        <div className="pa-toolbar__nav">
          <button type="button" className="btn-secondary" onClick={() => setDate(addDaysKey(date, -1))} aria-label="Previous day">‹</button>
          <button type="button" className="btn-secondary" onClick={() => setDate(realToday)}>Today</button>
          <button type="button" className="btn-secondary" onClick={() => setDate(addDaysKey(date, 1))} aria-label="Next day">›</button>
          <h1 style={{ margin: '0 0 0 8px', fontSize: 20 }}>{date === realToday ? `Today · ${dayHeading(fromDateKey(date))}` : dayHeading(fromDateKey(date))}</h1>
          {today.isFetching && <span className="muted" style={{ fontSize: 12 }}>updating…</span>}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Link to="/assistant/calendar" className="btn-secondary">Calendar</Link>
          <Link to="/assistant/tasks" className="btn-secondary">All tasks</Link>
        </div>
      </header>

      {d && d.needsReauth > 0 && (
        <div className="card pa-banner pa-banner--warn">
          <span>
            {d.needsReauth === 1 ? 'One account needs' : `${d.needsReauth} accounts need`} you to sign in again, so this view may be missing things.
          </span>
          <Link to="/assistant/connections">Fix on Connections</Link>
        </div>
      )}
      {message && (
        <div className="card pa-banner pa-banner--err" role="alert">
          <span>{message}</span>
          <button type="button" className="link-button" onClick={() => setMessage(null)}>Dismiss</button>
        </div>
      )}
      {today.isError && <div className="card pa-banner pa-banner--err">Could not load today. {errorMessage(today.error)}</div>}

      <div className="pa-today__grid">
        <section className="card">
          <h2 className="pa-today__heading">Schedule</h2>
          {d && d.events.length === 0 && <p className="muted" style={{ margin: 0 }}>Nothing on the calendar.</p>}
          {(d?.events ?? []).map((ev) => (
            <EventLine key={ev.id} ev={ev} />
          ))}
          {upcomingByDay.size > 0 && (
            <>
              <h3 className="pa-today__sub">Coming up</h3>
              {[...upcomingByDay.entries()].map(([key, list]) => (
                <div key={key} style={{ marginBottom: 6 }}>
                  <div className="muted" style={{ fontSize: 12 }}>{dayHeading(fromDateKey(key))}</div>
                  {list.map((ev) => (
                    <EventLine key={ev.id} ev={ev} compact />
                  ))}
                </div>
              ))}
            </>
          )}
        </section>

        <section className="card">
          <h2 className="pa-today__heading">Tasks</h2>
          <form
            className="pa-quickadd pa-quickadd--bare"
            onSubmit={(e) => {
              e.preventDefault();
              if (!quick.trim()) return;
              create.mutate({ title: quick.trim(), dueDate: date }, { onSuccess: () => setQuick(''), onError: (err) => setMessage(errorMessage(err)) });
            }}
          >
            <input type="text" placeholder={`Add a task for ${date === realToday ? 'today' : date}…`} value={quick} onChange={(e) => setQuick(e.target.value)} style={{ flex: 1 }} />
            <button type="submit" className="btn-primary" disabled={busy || !quick.trim()}>Add</button>
          </form>
          {d && d.overdue.length > 0 && (
            <>
              <h3 className="pa-today__sub pa-today__sub--overdue">Overdue · {d.overdue.length}</h3>
              {d.overdue.map((t) => (
                <TaskRow key={t.id} task={t} today={date} busy={busy} onToggle={toggle} onEdit={setEditing} onDelete={del} />
              ))}
            </>
          )}
          <h3 className="pa-today__sub">Due {date === realToday ? 'today' : date} · {d?.dueToday.length ?? 0}</h3>
          {d && d.dueToday.length === 0 && <p className="muted" style={{ margin: '0 0 8px' }}>Nothing due.</p>}
          {(d?.dueToday ?? []).map((t) => (
            <TaskRow key={t.id} task={t} today={date} busy={busy} onToggle={toggle} onEdit={setEditing} onDelete={del} />
          ))}
          {d && d.undated.length > 0 && (
            <>
              <h3 className="pa-today__sub">Someday</h3>
              {d.undated.map((t) => (
                <TaskRow key={t.id} task={t} today={date} busy={busy} onToggle={toggle} onEdit={setEditing} onDelete={del} />
              ))}
            </>
          )}
        </section>

        <section className="card">
          <h2 className="pa-today__heading" style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            Email <span className="muted" style={{ fontSize: 13 }}>{d?.attentionOpen ?? 0}</span>
            <Link to="/assistant/attention" style={{ marginLeft: 'auto', fontSize: 13 }}>Open email</Link>
          </h2>
          {d && d.attentionOpen === 0 && <p className="muted" style={{ margin: 0 }}>Nothing needs a reply.</p>}
          {(d?.attention ?? []).map((m) => (
            <Link key={m.id} to="/assistant/attention" className="pa-agenda__row pa-agenda__row--static pa-agenda__row--compact" style={{ textDecoration: 'none' }}>
              <span className="tag pa-feed__acct" style={{ background: m.color }}>{m.connectionLabel}</span>
              <span className="pa-agenda__subject" title={m.subject || '(no subject)'}>
                <strong>{m.folder === 'sent' ? m.toEmails[0] : (m.fromName || m.fromEmail)}</strong> · {m.subject || '(no subject)'}
              </span>
            </Link>
          ))}
        </section>
      </div>

      {editing && (
        <TaskEditor
          task={editing}
          busy={update.isPending}
          error={update.isError ? errorMessage(update.error) : null}
          onSave={(patch) => update.mutate({ id: editing.id, ...patch }, { onSuccess: () => setEditing(null) })}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function EventLine({ ev, compact }: { ev: AssistantEvent; compact?: boolean }) {
  return (
    <div className={`pa-agenda__row pa-agenda__row--static${compact ? ' pa-agenda__row--compact' : ''}`}>
      <span className="pa-dot" style={{ background: ev.color }} aria-hidden="true" />
      <span className="pa-agenda__time">{ev.isAllDay ? 'all day' : formatTimeRange(ev.startsAt, ev.endsAt)}</span>
      <span className="pa-agenda__subject" title={`${PROVIDER_LABELS[ev.provider] ?? ev.provider}${ev.location ? ` · ${ev.location}` : ''}`}>
        {ev.subject || '(no title)'}
      </span>
      {ev.location && !compact && <span className="muted pa-agenda__loc">{ev.location}</span>}
      {ev.matterRef && <span className="tag">{ev.matterRef}</span>}
      {ev.webLink && (
        <a href={ev.webLink} target="_blank" rel="noopener noreferrer" className="muted" style={{ fontSize: 12 }}>
          open
        </a>
      )}
    </div>
  );
}
